import { inject, injectable } from 'inversify';

import {
  TYPES as PROJECTS_STORES_TYPES,
  ProjectStoreInjector,
} from '@desygner/ui-features-projects';

import { zoom } from '../../config/constants';
import { TYPES } from '../../inversify.types';
import { IViewerStoreInjector } from '../../store';
import {
  arrowDownShortcut,
  arrowLeftShortcut,
  arrowRightShortcut,
  arrowUpShortcut,
  pinchZoomInShortcut,
  pinchZoomOutShortcut,
  zoomInShortcut,
  zoomOutShortcut,
} from './shortcuts';
import { wheelDownShortcut } from './shortcuts/wheel-down';
import { wheelUpShortcut } from './shortcuts/wheel-up';

export interface IKeyboardShortcutsManager {
  init: () => void;
}

export interface Shortcut {
  callBackFunction: () => void;
  key: Set<string>;
}

@injectable()
export class KeyboardShortcutsManager implements IKeyboardShortcutsManager {
  constructor(
    @inject(TYPES.Store.Viewer)
    private readonly viewerStore: IViewerStoreInjector,

    @inject(PROJECTS_STORES_TYPES.ProjectsStore)
    private readonly projectsStore: ProjectStoreInjector
  ) {}

  private readonly isMacLike: boolean = /(Mac|iPhone|iPod|iPad)/i.test(navigator.userAgent);

  private shortcuts: Shortcut[] = [];
  private readonly DELTA_LINE_MULTIPLIER = 8;
  private readonly DELTA_PAGE_MULTIPLIER = 24;
  private readonly MAX_WHEEL_DELTA = 24;
  private readonly WHEEL_ZOOM_SENSITIVITY = 1;
  private in_gesture = false;
  private zoomAtGestureStart = 100;

  private initialTouches: TouchList | null = null;
  private touchGestureState: {
    initialDistance: number;
    initialMidpoint: { x: number; y: number };
    zoomAtStart: number;
    pagePointX: number;
    pagePointY: number;
  } | null = null;

  // Centralized shortcuts map as a private attribute
  private readonly shortcutsMap: Record<string, Shortcut> = {
    ArrowRight: arrowRightShortcut,
    ArrowDown: arrowDownShortcut,
    ArrowLeft: arrowLeftShortcut,
    ArrowUp: arrowUpShortcut,
    ZoomIn: zoomInShortcut,
    ZoomOut: zoomOutShortcut,
    PinchZoomIn: pinchZoomInShortcut,
    PinchZoomOut: pinchZoomOutShortcut,
    WheelUp: wheelUpShortcut,
    WheelDown: wheelDownShortcut,
  };

  private initializeShortcuts = (shortcuts: Record<string, Shortcut>) => {
    for (const shortcut in shortcuts) {
      const { callBackFunction, key } = shortcuts[shortcut];

      this.shortcuts.push({
        key,
        callBackFunction,
      });
    }
  };

  init = () => {
    this.initializeShortcuts(this.shortcutsMap);

    document.addEventListener('keydown', this.handleKeyDown);

    document.addEventListener('wheel', this.handleWheel, { passive: false });

    document.addEventListener('gesturestart', this.handleGestureStart, {
      passive: false,
    });

    document.addEventListener('gesturechange', this.handleGestureChange, {
      passive: false,
    });

    document.addEventListener('gestureend', this.handleGestureEnd, {
      passive: false,
    });

    document.addEventListener('touchstart', this.handleTouchStart, {
      passive: false,
    });

    document.addEventListener('touchmove', this.handleTouchMove, {
      passive: false,
    });

    document.addEventListener('touchend', this.handleTouchEnd, {
      passive: false,
    });
  };

  private handleTouchStart = (event: TouchEvent) => {
    const isTwoFingerTouch = event.touches.length === 2;

    if (isTwoFingerTouch) {
      event.preventDefault();
      this.initialTouches = event.touches;
      const midpoint = this.midpoint(this.initialTouches);
      const state = this.viewerStore.getState();

      this.touchGestureState = {
        initialDistance: this.distance(this.initialTouches),
        initialMidpoint: midpoint,
        zoomAtStart: state.zoom,
        pagePointX: 0,
        pagePointY: 0,
      };

      // Calculate the point on content under the midpoint
      const currentScale = this.touchGestureState.zoomAtStart / 100;

      const cursorDocX = midpoint.x + window.scrollX;
      const cursorDocY = midpoint.y + window.scrollY;

      this.touchGestureState.pagePointX = cursorDocX / currentScale;
      this.touchGestureState.pagePointY = cursorDocY / currentScale;
    }
  };

  private handleTouchMove = (event: TouchEvent) => {
    const isTwoFingerTouch = event.touches.length === 2;

    if (isTwoFingerTouch && this.initialTouches && this.touchGestureState) {
      event.preventDefault();

      const newDistance = this.distance(event.touches);
      const scale = newDistance / this.touchGestureState.initialDistance;
      const newZoom = this.touchGestureState.zoomAtStart * scale;
      const newScale = newZoom / 100;
      const midpoint = this.touchGestureState.initialMidpoint;
      const state = this.viewerStore.getState();

      // Adjust window scroll position
      const newCursorDocX = this.touchGestureState.pagePointX * newScale;
      const newCursorDocY = this.touchGestureState.pagePointY * newScale;
      const newScrollX = newCursorDocX - midpoint.x;
      const newScrollY = newCursorDocY - midpoint.y;

      document.documentElement.scrollLeft = newScrollX;
      document.documentElement.scrollTop = newScrollY;
      state.setZoom(newZoom);
    }
  };

  private handleTouchEnd = (_event: TouchEvent) => {
    if (this.initialTouches && this.touchGestureState) {
      this.initialTouches = null;
      this.touchGestureState = null;
    }
  };

  private distance(touches: TouchList) {
    const firstTouch = touches[0];
    const secondTouch = touches[1];

    return Math.hypot(
      secondTouch.clientX - firstTouch.clientX,
      secondTouch.clientY - firstTouch.clientY
    );
  }

  private midpoint(touches: TouchList) {
    const firstTouch = touches[0];
    const secondTouch = touches[1];

    return {
      x: (firstTouch.clientX + secondTouch.clientX) / 2,
      y: (firstTouch.clientY + secondTouch.clientY) / 2,
    };
  }

  private gestureOrigin: {
    x: number;
    y: number;
    targetX: number;
    targetY: number;
  } | null = null;

  private handleGestureStart = (event: Event) => {
    const typedEvent = event as Event & {
      scale: number;
      clientX: number;
      clientY: number;
    };

    if (!this.in_gesture) {
      this.in_gesture = true;
      const state = this.viewerStore.getState();

      this.zoomAtGestureStart = state.zoom;

      // Store the gesture origin point
      const currentScale = this.zoomAtGestureStart / 100;
      const cursorX = typedEvent.clientX || window.innerWidth / 2;
      const cursorY = typedEvent.clientY || window.innerHeight / 2;

      const cursorDocX = cursorX + window.scrollX;
      const cursorDocY = cursorY + window.scrollY;

      this.gestureOrigin = {
        x: cursorX,
        y: cursorY,
        targetX: cursorDocX / currentScale,
        targetY: cursorDocY / currentScale,
      };
    }

    if (event.cancelable !== false) {
      event.preventDefault();
    }
  };

  private handleGestureChange = (event: Event) => {
    const typedEvent = event as Event & { scale: number };

    if (this.in_gesture && this.gestureOrigin) {
      const newZoom = this.zoomAtGestureStart * typedEvent.scale;
      const newScale = newZoom / 100;
      const state = this.viewerStore.getState();

      // For single page mode, adjust window scroll position
      const newCursorDocX = this.gestureOrigin.targetX * newScale;
      const newCursorDocY = this.gestureOrigin.targetY * newScale;
      const newScrollX = newCursorDocX - this.gestureOrigin.x;
      const newScrollY = newCursorDocY - this.gestureOrigin.y;

      // Apply scroll BEFORE zoom to ensure they happen in the same frame
      document.documentElement.scrollLeft = newScrollX;
      document.documentElement.scrollTop = newScrollY;
      state.setZoom(newZoom);
    }

    if (event.cancelable !== false) {
      event.preventDefault();
    }
  };

  private handleGestureEnd = (event: Event) => {
    if (this.in_gesture) {
      this.in_gesture = false;
      this.zoomAtGestureStart = 100;
      this.gestureOrigin = null;
    }

    if (event.cancelable !== false) {
      event.preventDefault();
    }
  };

  private handleKeyDown = (event: KeyboardEvent) => {
    const pressedKeys = this.getPressedKeys(event);

    if (!pressedKeys) return;

    this.processShortcuts(event, pressedKeys);
  };

  private zoomOnCursor = (event: WheelEvent, newZoom: number) => {
    const state = this.viewerStore.getState();
    const currentZoom = state.zoom;
    const currentScale = currentZoom / 100;

    const zoomConfig = { minZoom: zoom.min, maxZoom: zoom.max };
    const clampedZoom = Math.max(zoomConfig.minZoom, Math.min(zoomConfig.maxZoom, newZoom));

    const isZoomUnchanged = Math.abs(currentZoom - clampedZoom) < 0.01;

    if (isZoomUnchanged) {
      return;
    }

    if (state.pageMode === 'single') {
      const newScale = clampedZoom / 100;

      // adjust window scroll position
      const cursorX = event.clientX;
      const cursorY = event.clientY;
      const currentScrollX = window.scrollX;
      const currentScrollY = window.scrollY;

      // Calculate cursor position in the document
      const cursorDocX = cursorX + currentScrollX;
      const cursorDocY = cursorY + currentScrollY;

      // Calculate the point on the unscaled content under the cursor
      const contentPointX = cursorDocX / currentScale;
      const contentPointY = cursorDocY / currentScale;

      // Calculate where that point will be after new zoom
      const newCursorDocX = contentPointX * newScale;
      const newCursorDocY = contentPointY * newScale;

      // Calculate new scroll position to keep cursor over the same content point
      const newScrollX = newCursorDocX - cursorX;
      const newScrollY = newCursorDocY - cursorY;

      document.documentElement.scrollLeft = newScrollX;
      document.documentElement.scrollTop = newScrollY;
    }

    state.setZoom(clampedZoom);
  };

  private handlePinchZoom = (event: WheelEvent) => {
    if (event.cancelable !== false) {
      event.preventDefault();
    }

    const [, dy] = this.normalizeWheel(event);
    const currentZoom = this.viewerStore.getState().zoom;

    // Calculate scale factor with speedup and asymmetry correction
    const scaleFactor =
      dy <= 0
        ? 1 - (this.WHEEL_ZOOM_SENSITIVITY * dy) / 100
        : 1 / (1 + (this.WHEEL_ZOOM_SENSITIVITY * dy) / 100);

    const newZoom = currentZoom * scaleFactor;

    this.zoomOnCursor(event, newZoom);
  };

  private handleWheel = (event: WheelEvent) => {
    const isZoomModifierPressed = event.metaKey || event.ctrlKey;
    const pressedKeys = new Set<string>();

    if (!isZoomModifierPressed) {
      const { pageMode, selectedPageNumber, zoom } = this.viewerStore.getState();
      const { project, pages } = this.projectsStore.getState();

      const singlePageMode = pageMode === 'single';

      if (!singlePageMode || !project) return;

      const zoomLevel = zoom / 100;
      const { availHeight } = window.screen;
      const currentSelectedPage = project.currentProject?.pages?.[selectedPageNumber - 1];

      if (currentSelectedPage?.height && currentSelectedPage.height * zoomLevel > availHeight)
        return;

      const { deltaY, deltaX } = event;

      if (deltaY < 0 && Math.abs(deltaY) > Math.abs(deltaX)) {
        pressedKeys.add('WheelUp');
      }

      if (deltaY > 0 && Math.abs(deltaY) > Math.abs(deltaX)) {
        pressedKeys.add('WheelDown');
      }

      this.processShortcuts(event, pressedKeys);
      return;
    }

    this.handlePinchZoom(event);
  };

  private preventBrowserZoomDefaultBasedOnOS = (event: KeyboardEvent) => {
    const isPlusOrEqual = event.key === '-' || event.key === '=';

    if (this.isMacLike) {
      const isCMD = event.metaKey;

      if (isCMD && isPlusOrEqual) {
        event.preventDefault();
      }
    }

    // On non-Mac platforms, we check for Control key
    const isCtrl = event.ctrlKey;

    if (isCtrl && isPlusOrEqual) {
      event.preventDefault();
    }
  };

  private processShortcuts = (event: Event, pressedKeys: Set<string>) => {
    this.preventBrowserZoomDefaultBasedOnOS(event as KeyboardEvent);

    for (const shortcut of this.shortcuts) {
      if (!areEqualSets(shortcut.key, pressedKeys)) continue;

      event.preventDefault();

      shortcut.callBackFunction();

      break;
    }
  };

  private getPressedKeys = (event: KeyboardEvent): Set<string> | null => {
    if (!event.code) return null;

    const pressedKeys = new Set<string>();

    pressedKeys.add(this.translateKey(event.code));

    if (event.metaKey) pressedKeys.add('Control');

    if (event.altKey) pressedKeys.add('Alt');

    // On non-Mac platforms, add 'Control' to pressedKeys if the Control key is pressed.
    // This is because on Mac, the Command key is used instead of the Control key.
    if (event.ctrlKey && !this.isMacLike) pressedKeys.add('Control');

    return pressedKeys;
  };

  private translateKey(keyCodeName: string): string {
    if (['Equal', 'NumpadAdd'].includes(keyCodeName)) return 'Plus';

    if (['Minus', 'NumpadSubtract'].includes(keyCodeName)) return 'Minus';

    return keyCodeName;
  }

  private limit(delta: number, max_delta: number) {
    return Math.sign(delta) * Math.min(max_delta, Math.abs(delta));
  }

  private normalizeWheel(event: WheelEvent) {
    let dx = event.deltaX;
    let dy = event.deltaY;

    if (event.shiftKey && dx === 0) {
      [dx, dy] = [dy, dx];
    }

    if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
      dx *= this.DELTA_LINE_MULTIPLIER;
      dy *= this.DELTA_LINE_MULTIPLIER;
    } else if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
      dx *= this.DELTA_PAGE_MULTIPLIER;
      dy *= this.DELTA_PAGE_MULTIPLIER;
    }

    return [this.limit(dx, this.MAX_WHEEL_DELTA), this.limit(dy, this.MAX_WHEEL_DELTA)];
  }
}

function areEqualSets<T>(setA: Set<T>, setB: Set<T>): boolean {
  if (setA.size !== setB.size) {
    return false;
  }

  for (const item of setA) {
    if (!setB.has(item)) {
      return false;
    }
  }

  return true;
}
